/**
 * Domain types. Plain data in, plain data out — see Implementation_Brief §6.
 *
 * These are deliberately NOT Prisma types. The engine must be callable from the
 * browser with a payload assembled by an API endpoint, and from the server with
 * rows loaded by Prisma, and produce identical numbers from both.
 */

export type Obligation = 'MANDATORY' | 'RECOMMENDED';

export type ItemScope = 'PROJECT' | 'REPOSITORY' | 'ENVIRONMENT';

export type ItemState =
  | 'PENDING'
  | 'PARTIAL'
  | 'VERIFIED'
  | 'REJECTED'
  | 'NOT_APPLICABLE'
  | 'WAIVED';

export type TeamRole = 'PM' | 'TL' | 'DO' | 'SEC' | 'SUP' | 'PO';

export type ResultOrigin = 'FRESH' | 'INHERITED';

export type HostingModel = 'AZURE' | 'HYBRID' | 'ON_PREM';

/** §4.3: the only three permitted values. The importer rejects anything else. */
export type FreshnessDays = 365 | 180 | 90;

export type MaturityLevel = 'L1' | 'L2' | 'L3' | 'L4';

export type FreshnessStatus = 'FRESH' | 'EXPIRING_SOON' | 'EXPIRED';

/**
 * §6.1 — the ONLY flags an applicability expression may name.
 *
 * `hostingModel` and `usesManagedServices` are deliberately absent: they select
 * the evidence-expectation *variant* for C-01/C-05, they do not decide whether an
 * item applies. Keeping them out of this list is what stops that error being
 * reintroduced — the expression parser rejects any identifier not listed here.
 */
export const APPLICABILITY_FLAGS = [
  'processesPersonalData',
  'publiclyExposed',
  'exposesPublicApi',
  'wizInScope',
] as const;

export type ApplicabilityFlag = (typeof APPLICABILITY_FLAGS)[number];

/**
 * The technology area an item belongs to — one per item, never several.
 *
 * Assigned per subsection in the metadata sidecar and cascaded to its items, exactly like
 * `scope` and `ownerRole`. A project may narrow its scope to a set of these (see
 * `category-scope.ts`); that is a second, coarser scope mechanism beside the context
 * profile, not a replacement for it.
 *
 * A closed list for the same reason `APPLICABILITY_FLAGS` is one: the importer rejects
 * anything not named here, so a typo in the sidecar cannot quietly create a thirteenth
 * category that no project has selected.
 */
export const CHECKLIST_CATEGORIES = [
  'SOURCE_CONTROL',
  'INFRASTRUCTURE',
  'ACCESS_CONTROL',
  'DOCUMENTATION',
  'CI_CD',
  'DEPENDENCY_MANAGEMENT',
  'TESTING_QUALITY',
  'GOVERNANCE_REPORTING',
  'SECURITY',
  'MONITORING_OBSERVABILITY',
  'INCIDENT_SUPPORT',
  'BACKUP_DR',
] as const;

export type ChecklistCategory = (typeof CHECKLIST_CATEGORIES)[number];

/** Parsed applicability expression (§6.1). Stored as an AST, never eval'd. */
export type ContextExpr =
  | { readonly kind: 'flag'; readonly name: ApplicabilityFlag }
  | { readonly kind: 'not'; readonly operand: ContextExpr }
  | { readonly kind: 'and'; readonly left: ContextExpr; readonly right: ContextExpr }
  | { readonly kind: 'or'; readonly left: ContextExpr; readonly right: ContextExpr };

export interface RecordedValueField {
  readonly label: string;
  readonly unit: string;
  readonly type: 'number' | 'text' | 'duration';
}

/**
 * One of the 218 checklist items, as published in an immutable template version.
 *
 * `contextCondition === null` means "always applies". §4.2: `[C]` is a modifier on
 * the obligation, never a third obligation value.
 */
export interface ItemDefinition {
  readonly ref: string;
  readonly section: string;
  readonly subsection: string;
  readonly sortOrder: number;
  readonly statement: string;
  /**
   * What would satisfy this item — quoted to the auditor in the drawer.
   *
   * `null` means nobody has written one yet (§4.5 owns that authoring). It is reported
   * as a gap, in the importer and in the product, but it no longer blocks publication:
   * since an auditor may verify on an attestation (ADR 0008), a missing expectation is a
   * documentation gap rather than an unanswerable item. See ADR 0009.
   */
  readonly evidenceExpectation: string | null;
  readonly obligation: Obligation;
  readonly contextCondition: ContextExpr | null;
  readonly scope: ItemScope;
  readonly primaryRepoOnly: boolean;
  readonly freshnessDays: FreshnessDays;
  readonly inheritable: boolean;
  readonly ownerRole: TeamRole;
  /**
   * `null` means the template version carries no categorisation — every item of v1.0.
   * An uncategorised item can never be taken out of scope by a category selection.
   */
  readonly category: ChecklistCategory | null;
  readonly autoCheckId: string | null;
  readonly recordedValueFields?: readonly RecordedValueField[];
  readonly strategyRef?: string | null;
}

/** §5.2 — the six flags a project answers in the registration wizard. */
export interface ContextProfile {
  readonly processesPersonalData: boolean;
  readonly publiclyExposed: boolean;
  readonly exposesPublicApi: boolean;
  readonly wizInScope: boolean;
  readonly hostingModel: HostingModel;
  readonly usesManagedServices: boolean;
}

/** A waiver as the engine needs to see it — approval and expiry only. */
export interface WaiverView {
  readonly approved: boolean;
  /** null = no expiry recorded. An unexpired waiver satisfies a gate question. */
  readonly expiresAt: Date | null;
}

/** One item's answer inside one audit, reduced to what the engine needs. */
export interface ItemResultView {
  readonly itemRef: string;
  readonly state: ItemState;
  readonly verifiedAt: Date | null;
  readonly origin?: ResultOrigin;
  readonly evidenceCount?: number;
  readonly gapNote?: string | null;
  /**
   * Present when the item was verified on the auditor's own confirmation rather than a
   * linked artefact. Counted and disclosed separately — never hidden.
   */
  readonly attestation?: string | null;
  readonly waiver?: WaiverView | null;
}

/** One repository's answer to a repository-scoped item. */
export interface RepoResultView {
  readonly repositoryId: string;
  readonly state: ItemState;
  readonly isPrimary: boolean;
}

/** §5.1 — one of the 22 gate questions, with its item refs already expanded. */
export interface GateQuestionDefinition {
  readonly number: number;
  readonly question: string;
  readonly itemRefs: readonly string[];
}
